/* eslint-disable @typescript-eslint/require-await */
import {
    isConversationIDLoggingEnabled,
    isAiMateUnifiedHelpIntegrationPhase2Enabled,
    isUnifiedHelpStreamingEnabled,
} from '@helpCenter/feature-flags';
import type { Action } from 'react-sweet-state';
import { v4 as uuidv4 } from 'uuid';
import { doc, p } from '@atlaskit/adf-utils/builders';
import {
    type Message,
    type Metadata,
    type Sources,
    getUHUpdatedMessage,
    ExperienceType,
} from '@atlassian/conversation-assistant';
import type { UHStreamMessage } from '@atlassian/conversation-assistant/dist/types/services/assistance-service/types';
import { APP_CONTEXT } from '../../app/context';
import { DEFAULT_PIPELINE } from '../../common/constants';
import { converseWithAiMate, getChannelId, sendMessageStream } from '../../rest/ai-mate-converse';
import type { ProviderAuthDetail } from '../../rest/converse';
import { RATE_LIMITED_STATUS_CODE, converse } from '../../rest/converse';

import { storeFeedback, storeFeedbackAIMate } from '../../rest/store-feedback';
import { genIntId } from '../../utils/ai-mate/gen-id';
import { getAuthMessages } from '../../utils/ai-mate/get-auth-messages';
import { initialAssistantMessage } from '../../utils/ai-mate/get-initial-assistant-message';
import { getTotalHelpDesks } from '../../utils/ai-mate/get-total-helpdesks';
import { getTotalRequestForms } from '../../utils/ai-mate/get-total-requestforms';
import { getTotalSources } from '../../utils/ai-mate/get-total-sources';
import {
    updateAssistantMessageStatusToLoadingResponse,
    updateAssistantMessageStatusToTypingResponse,
    updateFinalMessage,
    updateKeywords,
    addAuthMessage,
    replaceLastAssistantMessage,
    addAssistantMessage,
    updateMessage,
    updateSources,
} from '../../utils/ai-mate/process-assistant-message';
import { getAnalyticsWebClient } from '../../utils/analytics';
import {
    sendTrackEventForFailure,
    sendTrackEventForFetchedResponse,
    sendUIEventForAskedQuestion,
} from '../../utils/analytics/conversation-analytics';
import { getProductIdentifierAttributes } from '../../utils/analytics/get-product-identifier-attributes';
import { fetchProviderAuthStatus } from '../../utils/provider-auth-status/provider-auth-status';
import { initialState, ASSISTANT_USER_TYPE, BOT_USER_TYPE, HELP_SEEKER_USER_TYPE } from './constants';
import { buildErrorMessageAdfNode } from './error-adf-builder';
import type {
    AssistantMessage,
    AuthenticationMessage,
    BotMessage,
    ChannelErrorMessage,
    ChatMessage,
    Conversation,
    HelpSeekerMessage,
    State,
} from './types';
import { FeedbackType } from './types';

const CHANNEL_EXPIRY_TIME = 1000 * 60 * 59; // 59mins

export const fetchChannelId =
    (): Action<State, void, void> =>
    async ({ getState, setState, dispatch }) => {
        try {
            const channelId = await getChannelId();
            const history = getState().conversation?.history || [];
            const currentTimer = getState().activeTimer;
            const analyticsClient = getAnalyticsWebClient();

            if (currentTimer) {
                clearTimeout(currentTimer);
            }

            const activeTimer = setTimeout(() => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                dispatch(addChannelErrorMessage('CHANNEL_EXPIRY'));

                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                analyticsClient.sendTrackEvent({
                    source: 'uhConversationScreen',
                    action: 'expired',
                    actionSubject: 'conversation',
                    attributes: {
                        conversationId: channelId,
                        ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
                    },
                });
            }, CHANNEL_EXPIRY_TIME);

            if (channelId) {
                setState({
                    activeTimer,
                    isLoading: false,
                    conversation: {
                        conversationId: channelId,
                        history: [...history],
                    },
                });
            } else {
                throw new Error('No channel id!');
            }
        } catch (e) {
            await dispatch(addChannelErrorMessage('CHANNEL_FAILURE'));
        }
    };

export const startNewConversationWithAiMate =
    (term: string, { accountId }: { accountId: string }): Action<State, void, void> =>
    async ({ getState, setState, dispatch }) => {
        setState({
            isLoading: true,
            conversation: {
                conversationId: null,
                history: [],
            },
        });
        await dispatch<Action<State>>(fetchChannelId());

        const conversation = getState().conversation;

        if (conversation && conversation.conversationId) {
            dispatch(addHelpseekerMessageOnlyForUI(term, { accountId }));
            /**
             * Mark conversation start when the first conversation message is sent
             */
            setState({
                conversationStartTime: Date.now(),
            });

            if (isUnifiedHelpStreamingEnabled()) {
                dispatch(fetchStreamingConversationWithAiMate(term, true));
            } else {
                dispatch(fetchConversationWithAiMate(term, true));
            }
        }
    };

export const startNewConversation =
    (term: string, { accountId }: { accountId: string }): Action<State, void, void> =>
    async ({ setState, dispatch }) => {
        setState({
            isLoading: false,
            conversation: {
                conversationId: uuidv4(),
                history: [],
            },
        });
        dispatch(addHelpseekerMessage(term, { accountId }, true));
    };

export const onChangeSearchAll =
    (): Action<State> =>
    async ({ getState, setState }) => {
        const searchAll = getState().searchAll;
        setState({
            searchAll: !searchAll,
        });
    };

export const onPipelineChange =
    (pipeline: string): Action<State> =>
    async ({ setState }) => {
        setState({
            pipeline,
        });
    };

export const setTesting =
    (isTesting: boolean): Action<State> =>
    async ({ setState }) => {
        setState({
            isTesting,
        });
    };

export const addChannelErrorMessage =
    (type: ChannelErrorMessage['messageType']): Action<State, void> =>
    ({ getState, setState }) => {
        let conversation = getState().conversation;
        if (!conversation) {
            throw new Error('No conversation!');
        }
        conversation = {
            conversationId: null,
            history: [
                ...conversation.history,
                {
                    userType: 'channelError',
                    messageType: type,
                },
            ],
        };
        setState({ conversation, isLoading: false });
    };

export const recordFeedbackForConversation =
    (
        id: string | undefined,
        {
            feedbackLabel,
            feedbackType,
            detailedFeedback,
        }: { feedbackLabel: string; feedbackType: FeedbackType; detailedFeedback?: string }
    ): Action<State, void> =>
    async ({ getState, setState }) => {
        const analyticsClient = getAnalyticsWebClient();
        const conversation = getState().conversation;
        if (!conversation || !conversation.conversationId) {
            throw new Error('No conversation!');
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        storeFeedback({
            feedbackLabel,
            detailedFeedback,
            messageId: id,
            cloudId: APP_CONTEXT.cloudId,
            feedback: feedbackType === FeedbackType.POSITIVE ? 'like' : 'dislike',
        });

        if (isAiMateUnifiedHelpIntegrationPhase2Enabled()) {
            storeFeedbackAIMate({
                feedbackLabel,
                channelId: conversation.conversationId,
                messageId: id,
                feedback: feedbackType === FeedbackType.POSITIVE ? 'LIKE' : 'DISLIKE',
            }).catch(() => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                analyticsClient.sendTrackEvent({
                    source: 'uhAiMateFeedbackRequest',
                    action: 'feedbackRequestFailed',
                    actionSubject: 'feedback',
                    actionSubjectId: conversation.conversationId || '',
                    attributes: {
                        isAiMate: true,
                        ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
                    },
                });
            });
        }

        const updatedConversationHistory = conversation.history.map((message) => {
            if (
                message.userType === BOT_USER_TYPE ||
                message.userType === HELP_SEEKER_USER_TYPE ||
                message.userType === ASSISTANT_USER_TYPE
            ) {
                const { messageId } = message.messageMetadata;
                if (messageId && messageId === id) {
                    return {
                        ...message,
                        messageMetadata: {
                            ...message.messageMetadata,
                            messageFeedback: {
                                feedbackType,
                                feedbackLabel,
                                detailedFeedback,
                            },
                        },
                    };
                }
            }
            return message;
        });

        const updatedConversation = {
            ...conversation,
            history: updatedConversationHistory,
        };
        setState({ conversation: updatedConversation });
    };

const getDefaultErrorState = (conversation: NonNullable<State['conversation']>): State['conversation'] => {
    const errorMessageContent = buildErrorMessageAdfNode();

    return {
        ...conversation,
        history: [
            ...conversation.history,
            {
                userType: 'error',
                messageContentAdf: JSON.stringify(doc(errorMessageContent)),
            },
        ],
    };
};

export const refetchConversationWithAiMate =
    (): Action<State, void, void> =>
    async ({ getState, dispatch }) => {
        const conversation = getState().conversation;
        if (!conversation || !conversation.conversationId) {
            return;
        }
        const history = conversation.history;

        const helpSeekerMessages = history.filter(
            (chatMessage) => chatMessage.userType === HELP_SEEKER_USER_TYPE
        ) as HelpSeekerMessage[];

        const term = helpSeekerMessages[helpSeekerMessages.length - 1].messageMetadata?.originalQuery as
            | string
            | undefined;

        if (term) {
            if (isUnifiedHelpStreamingEnabled()) {
                dispatch(fetchStreamingConversationWithAiMate(term));
            } else {
                dispatch(fetchConversationWithAiMate(term));
            }
        }
    };

export const fetchStreamingConversationWithAiMate =
    (message: string, showAuthenticationMessage?: boolean): Action<State, void, void> =>
    async ({ getState, setState, dispatch }) => {
        const { conversation, providerAuthStatus: providerAuthDetails } = getState();
        const analyticsClient = getAnalyticsWebClient();

        if (!conversation || !conversation.conversationId) {
            throw new Error('No conversation!');
        }
        try {
            setState({ isLoading: true });

            sendUIEventForAskedQuestion(
                analyticsClient,
                conversation.conversationId,
                conversation.history.length,
                message.length
            );

            const conversationStartTime = getState().conversationStartTime;
            if (conversationStartTime && Date.now() - conversationStartTime >= CHANNEL_EXPIRY_TIME) {
                try {
                    await dispatch(addChannelErrorMessage('CHANNEL_EXPIRY'));
                } catch (e) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    analyticsClient.sendTrackEvent({
                        source: 'uhAiMateRequest',
                        action: 'channel_expiry_failed',
                        actionSubject: 'conversation',
                        actionSubjectId: conversation.conversationId,
                        attributes: {
                            isAiMate: true,
                            ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
                        },
                    });
                }
                return;
            }

            const answerId = genIntId();

            let assistantMessage: AssistantMessage = initialAssistantMessage(answerId);

            let generator: AsyncGenerator<UHStreamMessage> | undefined;

            const startTime = performance.now();
            let finishTime;
            try {
                generator = await sendMessageStream(message, conversation.conversationId);
            } catch (e) {
                setState({
                    conversation: getDefaultErrorState(conversation),
                });
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                analyticsClient.sendTrackEvent({
                    source: 'uhAiMateRequest',
                    action: 'streaming_fetch_failed',
                    actionSubject: 'conversation',
                    actionSubjectId: conversation.conversationId,
                    attributes: {
                        isAiMate: true,
                        ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
                    },
                });
                return;
            }
            if (!generator) {
                return;
            }

            setState({
                conversation: addAssistantMessage(assistantMessage, conversation),
            });

            for await (const streamMessage of generator) {
                const { type, content } = getUHUpdatedMessage(
                    streamMessage,
                    assistantMessage.aiMateConversationResponse.message
                ) ?? { type: '' };
                let metadata: Metadata | undefined;
                let authMessages: AuthenticationMessage[];
                let updatedConversation: Conversation;
                let totalSources: number;
                let totalRequestForms: number;
                let totalHelpDesks: number;

                switch (type) {
                    case 'CONTENT_SEARCH':
                        assistantMessage = updateKeywords(assistantMessage, (content as string) ?? '');
                        assistantMessage = updateAssistantMessageStatusToLoadingResponse(assistantMessage);
                        setState({
                            conversation: replaceLastAssistantMessage(assistantMessage, conversation),
                        });
                        break;
                    case 'ANALYSING_STATE':
                        assistantMessage = updateSources(assistantMessage, content as Sources);
                        setState({
                            conversation: replaceLastAssistantMessage(assistantMessage, conversation),
                        });
                        break;
                    case 'ANSWER_PART':
                        assistantMessage = updateMessage(assistantMessage, content as Message);
                        assistantMessage = updateAssistantMessageStatusToTypingResponse(assistantMessage);

                        setState({
                            conversation: replaceLastAssistantMessage(assistantMessage, conversation),
                        });
                        setState({
                            isLoading: false,
                        });
                        break;
                    case 'PLUGIN_INVOCATION':
                        assistantMessage = updateMessage(assistantMessage, content as Message);
                        setState({
                            conversation: replaceLastAssistantMessage(assistantMessage, conversation),
                        });
                        break;
                    case 'FINAL_RESPONSE':
                        metadata = (content as Message)?.message_metadata;

                        authMessages = getAuthMessages(
                            showAuthenticationMessage ?? false,
                            providerAuthDetails,
                            metadata
                        );

                        assistantMessage = updateFinalMessage(
                            assistantMessage,
                            content as Message,
                            metadata?.isFallbackMessage ?? false,
                            authMessages.length ? false : true
                        );

                        updatedConversation = replaceLastAssistantMessage(assistantMessage, conversation);
                        updatedConversation = addAuthMessage(authMessages, updatedConversation);

                        setState({
                            conversation: updatedConversation,
                        });
                        finishTime = performance.now();

                        totalSources = getTotalSources(content as Message);

                        totalHelpDesks = getTotalHelpDesks(content as Message);

                        totalRequestForms = getTotalRequestForms(content as Message);

                        sendTrackEventForFetchedResponse(
                            analyticsClient,
                            finishTime - startTime,
                            metadata?.confidenceScore,
                            metadata?.isFallbackMessage,
                            conversation.conversationId,
                            totalSources,
                            totalHelpDesks,
                            totalRequestForms
                        );
                        break;
                    case 'ERROR':
                        setState({
                            conversation: getDefaultErrorState(conversation),
                        });
                        sendTrackEventForFailure(analyticsClient, conversation.conversationId, false);
                        break;
                    default:
                        break;
                }
            }
        } finally {
            setState({ isLoading: false });
        }
    };

export const fetchConversationWithAiMate =
    (message: string, showAuthenticationMessage?: boolean): Action<State, void, void> =>
    async ({ getState, setState, dispatch }) => {
        const conversation = getState().conversation;
        const providerAuthDetails = getState().providerAuthStatus;
        const analyticsClient = getAnalyticsWebClient();

        if (!conversation || !conversation.conversationId) {
            throw new Error('No conversation!');
        }

        try {
            setState({ isLoading: true });

            sendUIEventForAskedQuestion(
                analyticsClient,
                conversation.conversationId,
                conversation.history.length,
                message.length
            );

            const conversationStartTime = getState().conversationStartTime;
            if (conversationStartTime && Date.now() - conversationStartTime >= CHANNEL_EXPIRY_TIME) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                dispatch(addChannelErrorMessage('CHANNEL_EXPIRY'));

                return;
            }

            const response = await converseWithAiMate({
                message,
                channelId: conversation.conversationId,
            });
            if (response && response.data) {
                const aiMateConversationResponse = response.data;

                if (isConversationIDLoggingEnabled()) {
                    // Suppressing existing violation. Please fix this.
                    // eslint-disable-next-line no-console
                    console.log(
                        `Conversation ID: ${conversation.conversationId}, Message ID: ${aiMateConversationResponse.message.id}`
                    );
                }

                const metadata = aiMateConversationResponse.message.message_metadata;

                const authMessages = getAuthMessages(showAuthenticationMessage ?? false, providerAuthDetails, metadata);

                const assistantMessage: AssistantMessage = {
                    aiMateConversationResponse,
                    userType: 'assistant',
                    messageMetadata: {
                        messageId: aiMateConversationResponse.message.id.toString(),
                    },
                };

                const updatedBotReplies: AssistantMessage[] | AuthenticationMessage = authMessages.length
                    ? [assistantMessage]
                    : [
                          {
                              ...assistantMessage,
                              messageMetadata: { ...assistantMessage?.messageMetadata, isFeedbackRequired: true },
                          },
                      ];

                const latestConversation = getState().conversation;
                if (!latestConversation || !latestConversation.conversationId) {
                    // Conversation terminated before response received, or a new conversation started
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                analyticsClient.sendTrackEvent({
                    source: 'uhConversationScreen',
                    action: 'received',
                    actionSubject: 'glitch',
                    attributes: {
                        conversationId: conversation.conversationId,
                        ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
                    },
                });

                setState({
                    conversation: {
                        ...latestConversation,
                        history: [...latestConversation.history, ...updatedBotReplies, ...authMessages],
                    },
                    isAuthPromptVisible: !!authMessages.length,
                    providerAuthStatus: [],
                });
            } else {
                const currentConversation = getState().conversation;
                if (!currentConversation || currentConversation.conversationId !== conversation.conversationId) {
                    // Conversation terminated before response received, or a new conversation started
                    return;
                }

                if (response.status === RATE_LIMITED_STATUS_CODE) {
                    setState({
                        conversation: {
                            ...currentConversation,
                            history: [
                                ...currentConversation.history,
                                {
                                    userType: 'error',
                                    messageContentAdf: JSON.stringify(
                                        doc(
                                            p(
                                                "We're sorry, but our system has reached its maximum usage limit at the moment. Please wait a few minutes and try your request again. Thank you for your understanding."
                                            )
                                        )
                                    ),
                                },
                            ],
                        },
                    });
                } else {
                    setState({
                        conversation: getDefaultErrorState(currentConversation),
                    });
                }
            }
        } finally {
            setState({ isLoading: false });
        }
    };

export const fetchConversation =
    (showAuthenticationMessage?: boolean): Action<State, void, void> =>
    async ({ getState, setState }) => {
        const conversation = getState().conversation;
        const analyticsClient = getAnalyticsWebClient();
        const providerAuthDetails = getState().providerAuthStatus;
        if (!conversation || !conversation.conversationId) {
            throw new Error('No conversation!');
        }

        try {
            setState({ isLoading: true });

            const chatUpdates = await converse({
                cloudId: APP_CONTEXT.cloudId,
                conversationId: conversation.conversationId,
                messages: conversation.history.filter(
                    ({ userType }) => ['feedback', 'error', 'authentication'].indexOf(userType) === -1
                ) as (BotMessage | HelpSeekerMessage)[],
                searchAll: getState().searchAll,
                usePipeline: getState().isTesting ? getState().pipeline : DEFAULT_PIPELINE,
            });

            const currentConversation = getState().conversation;
            if (!currentConversation || currentConversation.conversationId !== conversation.conversationId) {
                // Conversation terminated before response received, or a new conversation started
                return;
            }

            if (chatUpdates.messages.length === 0) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                analyticsClient.sendTrackEvent({
                    source: 'uhConversationScreen',
                    action: 'received',
                    actionSubject: 'glitch',
                    attributes: {
                        conversationId: conversation.conversationId,
                        ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
                    },
                });

                if (chatUpdates.status === RATE_LIMITED_STATUS_CODE) {
                    setState({
                        conversation: {
                            ...currentConversation,
                            history: [
                                ...currentConversation.history,
                                {
                                    userType: 'error',
                                    messageContentAdf: JSON.stringify(
                                        doc(
                                            p(
                                                "We're sorry, but our system has reached its maximum usage limit at the moment. Please wait a few minutes and try your request again. Thank you for your understanding."
                                            )
                                        )
                                    ),
                                },
                            ],
                        },
                    });
                } else {
                    const errorMessageContent = buildErrorMessageAdfNode();
                    setState({
                        conversation: {
                            ...currentConversation,
                            history: [
                                ...currentConversation.history,
                                {
                                    userType: 'error',
                                    messageContentAdf: JSON.stringify(doc(errorMessageContent)),
                                },
                            ],
                        },
                    });
                }
            } else {
                if (isConversationIDLoggingEnabled()) {
                    const message = chatUpdates.messages[0];

                    if (message.userType === BOT_USER_TYPE) {
                        // Suppressing existing violation. Please fix this.
                        // eslint-disable-next-line no-console
                        console.log(
                            `Conversation ID: ${currentConversation.conversationId}, Message ID: ${
                                message.messageMetadata?.messageId ?? ''
                            }`
                        );
                    }
                }
                const authMessages: ChatMessage[] =
                    showAuthenticationMessage && providerAuthDetails?.length
                        ? providerAuthDetails.map((providerAuthDetail) => ({
                              userType: 'authentication',
                              userAccountId: null,
                              messageContentAdf: '',
                              messageMetadata: {
                                  providerAuthDetail,
                                  isFallbackMessage: chatUpdates.messages[0].messageMetadata.isFallbackMessage,
                              },
                          }))
                        : [];

                const updatedBotReplies = authMessages.length
                    ? chatUpdates.messages
                    : chatUpdates.messages.map((message) => ({
                          ...message,
                          messageMetadata: { ...message.messageMetadata, isFeedbackRequired: true },
                      }));

                setState({
                    conversation: {
                        ...currentConversation,
                        history: [...currentConversation.history, ...updatedBotReplies, ...authMessages],
                    },
                    isAuthPromptVisible: !!authMessages.length,
                    providerAuthStatus: [],
                });
            }
        } finally {
            setState({ isLoading: false });
        }
    };

export const addHelpseekerMessage =
    (
        term: string,
        { accountId }: { accountId: string },
        showAuthenticationMessage?: boolean
    ): Action<State, void, void> =>
    async ({ getState, dispatch }) => {
        const analyticsClient = getAnalyticsWebClient();
        dispatch(addHelpseekerMessageOnlyForUI(term, { accountId }));

        const conversation = getState().conversation;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        analyticsClient.sendUIEvent({
            source: 'uhConversationScreen',
            action: 'asked',
            actionSubject: 'question',
            attributes: {
                conversationId: conversation?.conversationId,
                totalMessageCount: conversation?.history.length,
                messageLength: term.length,
                ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
            },
        });

        dispatch(fetchConversation(showAuthenticationMessage));
    };

export const addHelpseekerMessageWithAiMate =
    (
        term: string,
        { accountId }: { accountId: string },
        showAuthenticationMessage?: boolean
    ): Action<State, void, void> =>
    async ({ dispatch }) => {
        dispatch(addHelpseekerMessageOnlyForUI(term, { accountId }));
        if (isUnifiedHelpStreamingEnabled()) {
            dispatch(fetchStreamingConversationWithAiMate(term, showAuthenticationMessage));
        } else {
            dispatch(fetchConversationWithAiMate(term, showAuthenticationMessage));
        }
    };

export const addHelpseekerMessageOnlyForUI =
    (term: string, { accountId }: { accountId: string }): Action<State, void, void> =>
    async ({ getState, setState }) => {
        let conversation = getState().conversation;

        if (!conversation) {
            throw new Error('No conversation!');
        }

        conversation = {
            ...conversation,
            history: [
                ...conversation.history,
                {
                    userType: 'helpseeker',
                    userAccountId: accountId,
                    messageContentAdf: JSON.stringify(doc(p(term))),
                    messageMetadata: {
                        originalQuery: term,
                    },
                },
            ],
        };

        setState({ conversation });
    };

export const setFeedbackCollectorConfig =
    (isVisible: boolean, messageId?: string | undefined): Action<State> =>
    async ({ getState, setState }) => {
        const { messageId: existingMessageId } = getState().feedbackCollectorConfig;
        setState({
            feedbackCollectorConfig: {
                isVisible,
                messageId: messageId || existingMessageId,
            },
        });
    };

export const clearConversation =
    (): Action<State> =>
    async ({ setState }) => {
        setState({
            ...initialState,
        });
    };

export const setProviderAuthStatus =
    (providerAuthStatus: ProviderAuthDetail[] | null = null): Action<State> =>
    async ({ setState }) => {
        if (providerAuthStatus === null) {
            const providerAuthStatuses = await fetchProviderAuthStatus();
            setState({ providerAuthStatus: providerAuthStatuses });
        } else {
            setState({ providerAuthStatus });
        }
    };
